import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi } from '../api/auth';
import type { AdminUser } from '../types';

interface AuthContextType {
  isAuthenticated: boolean;
  isReady: boolean;
  user: AdminUser | null;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (patch: Partial<AdminUser>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const STORAGE_KEY = 'homehero_admin_session';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [user, setUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.token && parsed?.user) {
          setUser(parsed.user);
          setIsAuthenticated(true);
        }
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setIsReady(true);
    }
  }, []);

  const login = async (email: string, password: string, remember = true) => {
    const result = await authApi.login(email, password);
    const store = remember ? localStorage : sessionStorage;
    const other = remember ? sessionStorage : localStorage;
    other.removeItem(STORAGE_KEY);
    store.setItem(STORAGE_KEY, JSON.stringify({ token: result.token, user: result.user }));
    setUser(result.user);
    setIsAuthenticated(true);
  };

  const clearSession = () => {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Always clear local session even if the API call fails
    } finally {
      clearSession();
    }
  };

  const updateProfile = (patch: Partial<AdminUser>) => {
    if (!user) return;
    const next = { ...user, ...patch };
    setUser(next);
    const raw = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const payload = JSON.stringify({ ...parsed, user: next });
    if (localStorage.getItem(STORAGE_KEY)) localStorage.setItem(STORAGE_KEY, payload);
    else sessionStorage.setItem(STORAGE_KEY, payload);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isReady, user, login, logout, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
